import {
	BiddersStateSetup,
	BiddersTargetingUpdater,
	ConsentManagementPlatformSetup,
	DirectTrafficPredictionValidatorSetup,
	ensureGeoAvailable,
	ExperimentBucketReporterSetup,
	InstantConfigSetup,
	LabradorSetup,
	LoadTimesSetup,
	MetricReporterSetup,
	NoAdsDetector,
	NoAdsMode,
	Peer39Experiment,
	BlockthroughReadershipLinkExperiment,
	PlatformContextSetup,
	PostAdStackPartnersSetup,
	PreloadedLibrariesSetup,
	SequentialMessagingSetup,
	SSExperiment,
	TrackingParametersSetup,
	TrackingSetup,
	UcpTargetingSetup,
} from '@platforms/shared';

import {
	communicationService,
	conditional,
	context,
	IdentitySetup,
	logVersion,
	parallel,
	ProcessPipeline,
	sequential,
} from '@wikia/ad-engine';
import { Injectable } from '@wikia/dependency-injection';
import { AD_ENGINE_CONFIGURED } from '../../communication/events/events-ad-engine';
import { basicContext } from './ad-context';
import { UcpDesktopAdsMode } from './modes/ucp-desktop-ads.mode';
import { UcpDesktopA9ConfigSetup } from './setup/context/a9/ucp-desktop-a9-config.setup';
import { UcpDesktopBaseContextSetup } from './setup/context/base/ucp-desktop-base-context.setup';
import { UcpDesktopPrebidConfigSetup } from './setup/context/prebid/ucp-desktop-prebid-config.setup';
import { UcpDesktopIcbOutOfPopularPagesSetup } from './setup/context/slots/ucp-desktop-icb-out-of-populars-page-setup';
import { UcpDesktopSlotsContextSetup } from './setup/context/slots/ucp-desktop-slots-context.setup';
import { UcpDesktopDynamicSlotsSetup } from './setup/dynamic-slots/ucp-desktop-dynamic-slots.setup';
import { UcpDesktopStickyRightRailExperimentWithMultipleICBSetup } from './setup/experiments/ucp-desktop-sticky-right-rail-experiment-with-multiple-icb-setup';
import { UcpDesktopTemplatesSetup } from './templates/ucp-desktop-templates.setup';
import { UcpDesktopNoSecondaryVideoExperiment } from './setup/experiments/ucp-desktop-no-secondary-video-experiment';
import { UcpDesktopFloorAdhesionWithBiddersExperiment } from './setup/experiments/ucp-desktop-floor-adhesion-with-bidders-experiment';

@Injectable()
export class UcpDesktopPlatform {
	constructor(
		private pipeline: ProcessPipeline,
		private noAdsDetector: NoAdsDetector,
	) {}

	execute(): void {
		logVersion();
		context.extend(basicContext);

		// Config
		this.pipeline.add(
			PlatformContextSetup,
			async () => await ensureGeoAvailable(),
			parallel(
				sequential(InstantConfigSetup, PreloadedLibrariesSetup),
				ConsentManagementPlatformSetup,
			),
			Peer39Experiment,
			BlockthroughReadershipLinkExperiment,
			UcpDesktopIcbOutOfPopularPagesSetup,
			ExperimentBucketReporterSetup,
			SSExperiment,
			UcpDesktopNoSecondaryVideoExperiment,
			UcpDesktopStickyRightRailExperimentWithMultipleICBSetup,
			TrackingParametersSetup,
			// Add this after TrackingParametersSetup so that we have access to window.fandomContext.tracking.pvNumberGlobal
			UcpDesktopFloorAdhesionWithBiddersExperiment,
			MetricReporterSetup,
			UcpDesktopBaseContextSetup,
			UcpDesktopSlotsContextSetup,
			IdentitySetup,
			UcpTargetingSetup,
			LoadTimesSetup,
			UcpDesktopPrebidConfigSetup,
			UcpDesktopA9ConfigSetup,
			UcpDesktopDynamicSlotsSetup,
			UcpDesktopTemplatesSetup,
			SequentialMessagingSetup,
			BiddersStateSetup,
			BiddersTargetingUpdater,
			LabradorSetup,
			DirectTrafficPredictionValidatorSetup,
			conditional(() => this.noAdsDetector.isAdsMode(), {
				yes: UcpDesktopAdsMode,
				no: NoAdsMode,
			}),
			TrackingSetup,
			() => communicationService.emit(AD_ENGINE_CONFIGURED),
			PostAdStackPartnersSetup,
		);

		this.pipeline.execute();
	}
}
